import { IconButton } from '@/components/IconButton'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { SearchField } from '@/components/SearchField'
import { Tooltip } from '@/components/Tooltip'
import { UnreadBadge } from '@/components/UnreadBadge'
import { pluralize } from '@/utils/pluralization-util'
import styles from './PrimaryControls.module.css'

export interface PrimaryControlsProps {
  /**
   * Whether the bulk actions menu is disabled, because no documents are
   * selected.
   */
  isMenuDisabled: boolean
  onDownloadSelected: () => void
  onDeleteSelected: () => void
  filterCount: number
  value: string
  onChange: (val: string) => void
  onShowFilterControls: () => void
}

export function PrimaryControls({
  isMenuDisabled,
  onDownloadSelected,
  onDeleteSelected,
  filterCount,
  value,
  onChange,
  onShowFilterControls,
}: PrimaryControlsProps) {
  const hasFilter = filterCount > 0
  const filterTooltip = hasFilter
    ? `${filterCount} ${pluralize('filter', filterCount)} selected`
    : 'Filter'
  const menuTooltip = 'Menu'

  return (
    <div className={styles.root}>
      <div className={styles.search}>
        <SearchField
          embedded
          appearance="toolbar"
          stretch
          value={value}
          onChange={onChange}
          testId="document-search-field"
        />
      </div>
      <div className={styles.actions}>
        <div className={styles.actionButton}>
          <Tooltip content={filterTooltip} placement="bottom">
            <IconButton
              name="filter"
              label="Filters"
              onPress={onShowFilterControls}
              testId="document-filter-controls-toggle"
              color="var(--grey600)"
            />
          </Tooltip>
          {filterCount > 0 && (
            <UnreadBadge className={styles.filterCount} count={filterCount} />
          )}
        </div>
        <div className={styles.actionButton}>
          <Dropdown
            autoDismiss
            position="bottom-end"
            isDisabled={isMenuDisabled}
            button={
              <Tooltip content={menuTooltip} placement="bottom">
                <IconButton
                  name="more"
                  label="More actions"
                  isDisabled={isMenuDisabled}
                  className={styles.menuToggle}
                  testId="document-management-table-menu-toggle"
                  color="var(--grey600)"
                />
              </Tooltip>
            }
          >
            <div>
              <DropdownTitle>More</DropdownTitle>
              <DropdownItem
                icon={<Icon name="download" size={24} />}
                onPress={onDownloadSelected}
                buttonTestId={'document-management-table-menu-download'}
              >
                Download selected
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="delete" size={24} />}
                onPress={onDeleteSelected}
                buttonTestId={'document-management-table-menu-delete'}
              >
                Delete selected
              </DropdownItem>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  )
}
